import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  PassageTaskLkCreateDto,
  type PassageTaskLkCreateInterface
} from '@zaoblako/shared/modules/pacs/dtos/passage-task-lk/passage-task-lk-create.dto';
import type { PassageTaskLkFindInterface } from '@zaoblako/shared/modules/pacs/dtos/passage-task-lk/passage-task-lk-find.dto';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { PersonalAccountPassageTaskService } from '@zaoblako/web/core/services/lk/personal-account-passage-task.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { InputNumberModule } from 'primeng/inputnumber';
import { Observable, Subscription } from 'rxjs';

@Component({
  selector: 'crm-lk-eu-passage-task-form',
  host: { class: 'frm-filters-ui-v2' },
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    SpinnerComponent,
    ButtonComponent,
    JoinPipe,
    DatepickerComponent,
    InputTextComponent,
    InputNumberModule,
    FormsModule
  ],
  templateUrl: './lk-eu-passage-task-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LkEuPassageTaskFormComponent
  extends BaseFormComponent<PassageTaskLkCreateInterface>
  implements OnInit, OnDestroy {
  @Input() public readonly index: number;
  public task = <PassageTaskLkCreateInterface>{ repeat: 0 };
  public dto = PassageTaskLkCreateDto;

  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  private formSubscription$: Subscription = new Subscription();

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly personalAccountPassageTaskService: PersonalAccountPassageTaskService,
    private readonly errorService: ErrorService,
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    const formData = this.formStoreService.getState.forms[this.index]?.data;
    if (formData?.date) {
      this.task.date = formData.date;
    }
    if (formData && 'name' in formData) {
      this.task.name = formData.name;
    }

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public ngOnDestroy(): void {
    this.formSubscription$?.unsubscribe();
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.personalAccountPassageTaskService
      .findById<PassageTaskLkFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (task) => {
          this.task = task;

          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveTask() {
    if (this.checkValidate(this.task).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.personalAccountPassageTaskService
          .update<PassageTaskLkCreateInterface, PassageTaskLkFindInterface>(this.task)
          .subscribe({
            next: (task) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`Заявка ${task?.numberWithPrefix} обновлена`);
              this.formStoreService.saveNext(task, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.personalAccountPassageTaskService.create<PassageTaskLkFindInterface>(this.task).subscribe({
          next: (task) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Заявка создана');
            if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
              this.formStoreService.closeSidebar(this.index);
              this.router
                .navigate(['/lk/external-user/passage-task/card/' + task.numberWithPrefix])
                .catch(console.error);
            } else {
              this.formStoreService.saveNext(task, this.index);
            }
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }
}
